import { useParams } from 'react-router-dom';
import { useCamper } from '../hooks/useCamper';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './CamperPage.css';
import { extras } from '../data/extras';
import { useMediaQuery } from 'react-responsive';

export function CamperPage() {
  const { slug } = useParams();
  const { camper, isLoading } = useCamper(slug);
  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });

  useEffect(() => {
  }, [isSmallScreen]);

  if (isLoading || !camper) return <div>Loading...</div>;

  // Extract main image URL from Contentful asset (if available)
  const mainImageUrl = camper.mainImage && camper.mainImage.fields 
    ? camper.mainImage.fields.file.url 
    : camper.mainImage;

  // Gallery images: using camper.galleryImages field from Contentful
  const galleryUrls = (camper.galleryImages || []).map(image =>
    image && image.fields ? image.fields.file.url : image
  );

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: true,
          dots: true
        }
      }
    ]
  };

  return (
    <div className="bg-white">
      {/* Hero Section with Main Image and Title */}
      <div className="relative h-screen">
        <img
          src={mainImageUrl}
          alt={camper.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {camper.name}
            </h1>
            <p className="text-xl md:text-2xl text-white max-w-2xl mb-12">
              {camper.shortDescription}
            </p>
            
            {/* Technical Specs */}
            <div className="bg-black bg-opacity-30 backdrop-blur-sm rounded-2xl p-6 md:p-8 inline-block max-w-3xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                <div className="border-l-2 border-brand pl-4">
                  <p className="text-brand font-medium uppercase tracking-wider text-sm">Modelo</p>
                  <p className="text-white text-lg font-semibold mt-1">
                    {camper.technicalSpecs && camper.technicalSpecs.model}
                  </p>
                </div>
                <div className="border-l-2 border-brand pl-4">
                  <p className="text-brand font-medium uppercase tracking-wider text-sm">Potencia</p>
                  <p className="text-white text-lg font-semibold mt-1">
                    {camper.technicalSpecs && camper.technicalSpecs.power}
                  </p>
                </div>
                <div className="border-l-2 border-brand pl-4">
                  <p className="text-brand font-medium uppercase tracking-wider text-sm">Año</p>
                  <p className="text-white text-lg font-semibold mt-1">
                    {camper.technicalSpecs && camper.technicalSpecs.year}
                  </p>
                </div>
                <div className="border-l-2 border-brand pl-4">
                  <p className="text-brand font-medium uppercase tracking-wider text-sm">Combustible</p>
                  <p className="text-white text-lg font-semibold mt-1">
                    {camper.technicalSpecs && camper.technicalSpecs.fuel}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery and Pricing Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Gallery Carousel */}
            <div className="w-full md:w-3/5">
              <Slider {...carouselSettings}>
                {galleryUrls.map((imgUrl, index) => (
                  <div key={index} className="relative aspect-[4/3]">
                    <img
                      src={imgUrl}
                      alt={`${camper.name} - Vista ${index + 1}`}
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                ))}
              </Slider>
            </div>

            {/* Pricing Section */}
            <div className="w-full md:w-2/5">
              <h2 className="text-3xl font-bold mb-8">Precio</h2>
              <div className="space-y-4">
                <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl hover:border-brand transition-colors duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Temporada alta</h3>
                    <span className="text-2xl font-bold text-brand">
                      {camper.pricing && camper.pricing.highSeason ? camper.pricing.highSeason[1] : ''}€/día
                    </span>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    <p className="text-sm">Período: {camper.pricing && camper.pricing.highSeason ? camper.pricing.highSeason[0] : ''}</p>
                    <p className="text-sm">Mínimo {camper.pricing && camper.pricing.highSeason ? camper.pricing.highSeason[2] : ''} días</p>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl hover:border-brand transition-colors duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Temporada media</h3>
                    <span className="text-2xl font-bold text-brand">
                      {camper.pricing && camper.pricing.midSeason ? camper.pricing.midSeason[1] : ''}€/día
                    </span>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    <p className="text-sm">Período: {camper.pricing && camper.pricing.midSeason ? camper.pricing.midSeason[0] : ''}</p>
                    <p className="text-sm">Mínimo {camper.pricing && camper.pricing.midSeason ? camper.pricing.midSeason[2] : ''} días</p>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl hover:border-brand transition-colors duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Temporada baja</h3>
                    <span className="text-2xl font-bold text-brand">
                      {camper.pricing && camper.pricing.lowSeason ? camper.pricing.lowSeason[1] : ''}€/día
                    </span>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    <p className="text-sm">Período: {camper.pricing && camper.pricing.lowSeason ? camper.pricing.lowSeason[0] : ''}</p>
                    <p className="text-sm">Mínimo {camper.pricing && camper.pricing.lowSeason ? camper.pricing.lowSeason[2] : ''} días</p>
                  </div>
                </div>

                <div className="mt-6 text-sm text-gray-600">
                  <p>
                    Estos precios incluyen 100km/día con opción de aumentar kilometraje.&nbsp;
                    <Link 
                      to="/condiciones-alquiler" 
                      className="text-brand hover:text-brand/80 font-medium underline-offset-4 hover:underline transition-all"
                    >
                      Revisa todas las condiciones de alquiler
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* New WhatsApp CTA Button */}
          <div className="mt-12 text-center">
            <a
              href="https://wa.me/yourphonenumber"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand/90 text-white font-bold py-4 px-8 rounded-xl text-lg transition-colors duration-300 shadow-lg hover:shadow-xl"
            >
              <svg 
                className="w-6 h-6" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
              </svg>
              Reserva ahora por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8">Qué incluye</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold text-xl mb-4">Exterior</h3>
              <ul className="space-y-3">
                {camper.features && camper.features.exterior && camper.features.exterior.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-xl mb-4">Interior</h3>
              <ul className="space-y-3">
                {camper.features && camper.features.interior && camper.features.interior.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-xl mb-4">Confort</h3>
              <ul className="space-y-3">
                {camper.features && camper.features.comfort && camper.features.comfort.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Extras Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8">Extras disponibles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {extras.map((extra) => (
              <div 
                key={extra.id}
                className="bg-white border-2 border-gray-100 p-6 rounded-2xl hover:border-brand transition-colors duration-300"
              >
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-semibold mb-2">{extra.name}</h3>
                  {extra.description && (
                    <p className="text-sm text-gray-600 mb-4">{extra.description}</p>
                  )}
                  <div className="mt-auto">
                    <p className="text-xl font-bold text-brand">
                      {extra.price}€
                      {extra.priceType === 'daily' && <span className="text-sm font-normal text-gray-600">/día</span>}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-600 text-center">
            Todos los extras son opcionales y se pueden añadir al hacer la reserva
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">
            ¿Listo para empezar la aventura?
          </h2>
          <a
            href="https://wa.me/yourphonenumber"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-brand font-bold py-4 px-8 rounded-lg text-lg hover:bg-gray-100 transition-colors"
          >
            Reserva ahora por WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
} 